"use client";

import { InfoCircledIcon } from "@radix-ui/react-icons";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { useCallback, useMemo, useState } from "react";
import { useMedia } from "react-use";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCreateNotes } from "@/features/ai-notetaker/api/use-create-notes";
import { AnkiImportDropzone } from "@/features/files/components/anki-import-dropzone";
import { PasteTextForm } from "@/features/files/components/paste-text-form";
import { YouTubeUrlForm } from "@/features/files/components/youtube-url-form";
import { useUserPlanQuery } from "@/features/users/api/user-plan-options";
import {
  getSteps,
  UPLOAD_METHODS,
  type UploadMethodValue,
} from "@/lib/constants";
import { useFreeUploadLimit } from "@/lib/hooks/use-free-upload-limit";
import usePricingModal from "@/lib/hooks/use-pricing-modal";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import { UploadDropZone } from "../../files/components/upload-dropzone";
import useNewStudySetModal from "../hooks/use-new-studyset-modal";

interface NewStudySetModalProps {
  empty?: boolean;
  variant?: "mobile";
}

type StepKey = "chooseMethod" | "uploadContent" | "upgrade";

export const NewStudySetDrawerModal = ({
  empty,
  variant,
}: NewStudySetModalProps) => {
  const { onClose, isOpen } = useNewStudySetModal();
  const [uploadMethod, setUploadMethod] = useState<UploadMethodValue | null>(
    null,
  );
  const STEPS = useMemo(() => getSteps(uploadMethod), [uploadMethod]);
  const [activeStep, setActiveStep] = useState(1);
  const [filePhase, setFilePhase] = useState<"upload" | "select_sections">(
    "upload",
  );
  const t = useI18n();
  const router = useRouter();
  const { onOpen: onOpenPricing } = usePricingModal();
  const freeUploadLimit = useFreeUploadLimit();
  const isDesktop = useMedia("(min-width: 768px)");

  const {
    data: userPlanData,
    status: userPlanStatus,
    error: _error,
    refetch,
  } = useUserPlanQuery();
  const createNotesMutation = useCreateNotes();
  const resetEverything = useCallback(
    (options: { closeDialog?: boolean; navigate?: boolean } = {}) => {
      if (options.closeDialog) onClose();
      setActiveStep(1);
      setUploadMethod(null);
    },
    [],
  );

  const handleUploadComplete = () => {
    resetEverything({ closeDialog: true, navigate: true });
  };

  const handleUploadMethod = (method: UploadMethodValue) => {
    if (
      (method === "file_upload" ||
        method === "paste_text" ||
        method === "youtube_url") &&
      userPlanStatus === "success"
    ) {
      if (
        !userPlanData?.isSubscribed &&
        userPlanData?.generationCount >= freeUploadLimit
      ) {
        onOpenPricing({
          upgradeReason:
            "Upgrade to Studygenie Premium for unlimited AI study set generations!",
          description:
            "The free plan will continue to allow you to create study sets manually.",
          paywallType: method,
        });
        posthog.capture("paywall_shown", {
          userId: userPlanData.userId,
          paywallType: method,
        });
        return;
      }
      setUploadMethod(method);
      setActiveStep(2);
    } else if (method === "anki_import") {
      setUploadMethod(method);
      setActiveStep(2);
    } else if (method === "create_manually") {
      resetEverything({ closeDialog: true, navigate: false });
      router.push("/studysets/new");
    } else if (method === "ai_notetaker") {
      createNotesMutation.mutate(
        {
          name: "Untitled Note",
        },
        {
          onSuccess: () => {
            resetEverything({ closeDialog: true, navigate: false });
          },
        },
      );
    }
  };

  const getStepTranslation = (
    step: number,
    filePhase: "upload" | "select_sections",
    type: "title" | "description",
  ) => {
    if (step === 2 && filePhase === "select_sections" && type === "title") {
      return t("tableOfContentsForm.selectSections");
    }
    if (
      step === 2 &&
      filePhase === "select_sections" &&
      type === "description"
    ) {
      return t("tableOfContentsForm.selectSectionsDescription");
    }
    const stepKey = STEPS[step - 1]?.key as StepKey;
    if (!stepKey) return "";

    const translationKey =
      `newStudySetDrawer.steps.${stepKey}.${type}` as const;
    return t(translationKey);
  };

  const renderContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
            <TooltipProvider>
              {UPLOAD_METHODS.map((method) => (
                <Tooltip key={method.title} delayDuration={300}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() =>
                        method.active && handleUploadMethod(method.value)
                      }
                      disabled={!method.active}
                      className={cn(
                        "w-full rounded-lg p-3 gap-4 flex items-center border",
                        method.active
                          ? "hover:border-brand-primary"
                          : "opacity-60 cursor-default",
                      )}
                    >
                      <div
                        className={cn(
                          "size-9 rounded-md grid place-items-center bg-muted",
                        )}
                      >
                        {method.icon}
                      </div>
                      <h2 className={cn("text-sm font-medium")}>
                        {t(
                          `newStudySetDrawer.uploadMethods.${method.value}.title`,
                        )}
                      </h2>
                      {!method.active && (
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0 ml-auto"
                        >
                          {t("comingSoon")}
                        </Badge>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t(
                      `newStudySetDrawer.uploadMethods.${method.value}.description`,
                    )}
                  </TooltipContent>
                </Tooltip>
              ))}
            </TooltipProvider>
          </div>
        );
      case 2:
        switch (uploadMethod) {
          case "file_upload":
            return (
              <div className="space-y-2">
                <UploadDropZone
                  resetForm={handleUploadComplete}
                  className="h-52"
                  onPhaseChange={(p) => setFilePhase(p)}
                />
                {filePhase === "upload" && (
                  <Button onClick={() => setActiveStep(1)} variant="ghost">
                    {t("newStudySetDrawer.buttons.goBack")}
                  </Button>
                )}
              </div>
            );
          case "paste_text":
            return (
              <PasteTextForm
                handleComplete={handleUploadComplete}
                cancel={() => setActiveStep(1)}
                cancelText={t("newStudySetDrawer.buttons.goBack")}
              />
            );
          case "youtube_url":
            return (
              <YouTubeUrlForm
                handleComplete={handleUploadComplete}
                cancel={() => setActiveStep(1)}
                cancelText={t("newStudySetDrawer.buttons.goBack")}
              />
            );
          case "anki_import":
            return (
              <div className="space-y-2">
                <AnkiImportDropzone
                  resetForm={handleUploadComplete}
                  className="h-52"
                />
                <Button onClick={() => setActiveStep(1)} variant="ghost">
                  {t("newStudySetDrawer.buttons.goBack")}
                </Button>
              </div>
            );
          default:
            return null;
        }
      case 3:
        return (
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() =>
                resetEverything({ closeDialog: true, navigate: true })
              }
            >
              {t("newStudySetDrawer.buttons.continueWithFree")}
            </Button>
            <Button
              onClick={() => {
                onOpenPricing({
                  upgradeReason:
                    "Upgrade to Studygenie Premium for unlimited AI study set generations!",
                  description:
                    "By upgrading you will get questions from your entire file and full access to AI superpowers.",
                });
              }}
            >
              {t("newStudySetDrawer.buttons.upgradeToPremium")}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  if (isDesktop) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={() =>
          resetEverything({ closeDialog: true, navigate: false })
        }
      >
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          className={cn(
            "focus:outline-none focus:ring-0 focus:ring-offset-0",
            "ring-0 ring-offset-0",
            "!outline-none",
          )}
        >
          <div className="flex flex-col gap-6">
            <ProgressIndicator activeStep={activeStep} steps={STEPS} />
            <div className="flex flex-col gap-6">
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <DialogTitle className="font-semibold text-lg leading-4">
                    {getStepTranslation(activeStep, filePhase, "title")}
                  </DialogTitle>
                  {!userPlanData?.isSubscribed && (
                    <TooltipProvider>
                      <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                          <InfoCircledIcon className="size-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent align="start" className="max-w-sm">
                          {t("newStudySetDrawer.freePlanInfo")}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <DialogDescription>
                  {getStepTranslation(activeStep, filePhase, "description")}
                </DialogDescription>
              </DialogHeader>
              {userPlanStatus === "pending" ? (
                <Loader2 className="animate-spin size-6 text-blue-500 mx-auto" />
              ) : userPlanStatus === "error" ? (
                <div className="flex flex-col gap-4">
                  <p className="text-sm font-medium text-destructive">
                    {t("newStudySetDrawer.errors.generic")}
                  </p>
                  <Button onClick={() => refetch()}>
                    {t("newStudySetDrawer.buttons.retry")}
                  </Button>
                </div>
              ) : (
                renderContent()
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={() =>
        resetEverything({ closeDialog: true, navigate: false })
      }
    >
      <DrawerContent className="h-[95%]">
        <div className="space-y-4">
          <DrawerHeader>
            <div className="py-4">
              <ProgressIndicator activeStep={activeStep} steps={STEPS} />
            </div>
            <DrawerTitle className="font-semibold text-lg leading-4">
              {getStepTranslation(activeStep, filePhase, "title")}
            </DrawerTitle>
            <DrawerDescription>
              {getStepTranslation(activeStep, filePhase, "description")}
            </DrawerDescription>
          </DrawerHeader>
        </div>
        <ScrollArea className="overflow-y-auto h-full">
          <div className="flex flex-col gap-6 h-full px-8 pb-8">
            {userPlanStatus === "pending" ? (
              <Loader2 className="animate-spin size-6 text-blue-500 mx-auto" />
            ) : userPlanStatus === "error" ? (
              <div className="flex flex-col gap-4">
                <p className="text-sm font-medium text-destructive">
                  {t("newStudySetDrawer.errors.generic")}
                </p>
                <Button onClick={() => refetch()}>
                  {t("newStudySetDrawer.buttons.retry")}
                </Button>
              </div>
            ) : (
              renderContent()
            )}
          </div>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
};

const ProgressIndicator = ({
  activeStep,
  steps,
}: {
  activeStep: number;
  steps: ReturnType<typeof getSteps>;
}) => (
  <div className="flex items-center gap-2 relative">
    {steps.slice(0, 2).map(({ step }) => (
      <div
        key={step}
        className={cn(
          "h-1 rounded-lg relative overflow-hidden",
          step === activeStep ? "w-8" : "w-4",
        )}
      >
        <div className="absolute w-full h-full bg-border" />
        <motion.div
          className="absolute w-full h-full bg-brand-primary"
          initial={{ x: "-100%" }}
          animate={{ x: step === activeStep ? "0%" : "-100%" }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        />
      </div>
    ))}
  </div>
);
